'use client';

import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Skeleton,
  EmptyState,
} from '@/ui';
import { CONTACT_SORT_OPTIONS } from '@lib/enums';
import ContactRow from './parts/ContactRow';
import type { Contact } from './parts/ContactRow';

const SKELETON_ROWS = 8;

interface SortBy {
  property: string;
  direction: 'asc' | 'desc';
}

interface ContactListProps {
  contacts?: Contact[];
  sortBy: SortBy;
  setSortBy: (sortBy: SortBy) => void;
  openEditDialog: (contact: Contact) => void;
  isLoading: boolean;
}

export default function ContactList({ contacts, sortBy, setSortBy, openEditDialog, isLoading }: ContactListProps) {
  const handleSortBy = (property: string) => {
    if (sortBy.property === property) {
      setSortBy({ property, direction: sortBy.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      setSortBy({ property, direction: 'asc' });
    }
  };

  const getSortDirection = (property: string) => {
    if (sortBy.property !== property) return null;
    return sortBy.direction;
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <Table hoverable style={{ tableLayout: 'fixed', width: '100%', minWidth: 798 }}>
        <TableHead>
          <TableRow>
            <TableHeader
              sortable
              sortDirection={getSortDirection(CONTACT_SORT_OPTIONS.FIRST_NAME)}
              onSort={() => handleSortBy(CONTACT_SORT_OPTIONS.FIRST_NAME)}
              style={{ width: '200px' }}
            >
              Name
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={getSortDirection(CONTACT_SORT_OPTIONS.COMPANY)}
              onSort={() => handleSortBy(CONTACT_SORT_OPTIONS.COMPANY)}
              style={{ width: '200px' }}
            >
              Company
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={getSortDirection(CONTACT_SORT_OPTIONS.LAST_CONTACTED_AT)}
              onSort={() => handleSortBy(CONTACT_SORT_OPTIONS.LAST_CONTACTED_AT)}
              style={{ width: '140px' }}
            >
              Last Contacted
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={getSortDirection(CONTACT_SORT_OPTIONS.UPDATED_AT)}
              onSort={() => handleSortBy(CONTACT_SORT_OPTIONS.UPDATED_AT)}
              style={{ width: '120px' }}
            >
              Updated
            </TableHeader>
            <TableHeader style={{ textAlign: 'right', width: '90px' }}></TableHeader>
            <TableHeader style={{ width: '48px' }}></TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            Array.from({ length: SKELETON_ROWS }).map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <Skeleton width={130} height={14} />
                    <Skeleton width={90} height={11} />
                  </div>
                </TableCell>
                <TableCell><Skeleton width={100} height={14} /></TableCell>
                <TableCell><Skeleton width={80} height={14} /></TableCell>
                <TableCell><Skeleton width={70} height={14} /></TableCell>
                <TableCell />
                <TableCell />
              </TableRow>
            ))
          ) : !contacts?.length ? (
            <TableRow>
              {/* Span all 6 columns so the empty state centers across the full table. */}
              <TableCell colSpan={6} style={{ padding: '24px 16px' }}>
                <EmptyState
                  icon="🔍"
                  title="No contacts found"
                  description="Try adjusting your search or filters to find what you're looking for."
                />
              </TableCell>
            </TableRow>
          ) : (
            contacts.map((contact) => (
              <ContactRow
                key={contact.id}
                contact={contact}
                openForEdit={openEditDialog}
              />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
